/**
 * VoiceRecorder Component - Voice Recording Controls
 * Handles speech recognition and recording state
 */
import React, { memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Volume2 } from "lucide-react";

const VoiceRecorder = memo(({ 
  isRecording, 
  onStartRecording, 
  onStopRecording,
  transcript,
  disabled = false 
}) => {
  const handleToggle = useCallback(() => {
    if (isRecording) {
      onStopRecording();
    } else {
      onStartRecording();
    }
  }, [isRecording, onStartRecording, onStopRecording]);

  return (
    <div className="space-y-3">
      {/* Recording Button */}
      <Button
        onClick={handleToggle}
        disabled={disabled}
        size="lg"
        className={`w-full h-14 text-lg font-medium transition-all duration-300 ${
          isRecording 
            ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
            : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
        }`}
        data-testid="voice-record-btn"
      >
        {isRecording ? (
          <>
            <MicOff className="w-5 h-5 mr-2" />
            Stop Recording
          </>
        ) : (
          <>
            <Mic className="w-5 h-5 mr-2" />
            Start Voice Answer
          </>
        )}
      </Button>

      {/* Recording Status Indicator */}
      {isRecording && (
        <div className="flex items-center justify-center gap-2 text-red-600">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Recording in progress...</span>
          <Volume2 className="w-4 h-4 animate-bounce" />
        </div>
      )}

      {/* Transcript Preview */}
      {transcript && (
        <div className="p-3 bg-gray-50 rounded-lg border">
          <p className="text-xs text-gray-500 mb-1">Live Transcript:</p>
          <p className="text-sm text-gray-700 line-clamp-3">{transcript}</p>
        </div>
      )}
    </div>
  );
});

VoiceRecorder.displayName = "VoiceRecorder";

export default VoiceRecorder;
